// hooks/usePremium.ts — hook for premium tier/benefit MARKETING COPY.
//
// Pure read-only (no store — tiers are static display copy). Uses
// React.useState + useEffect to load async data from premiumService.
// Components must NOT import premiumService directly. For real subscription
// STATE (tier, status, renewal), see hooks/useBilling.ts instead.

import { useState, useEffect } from 'react';
import { premiumService } from '@/core/services/premiumService';
import type { PremiumTier, UltraBenefit } from '@/core/models/premium';

export interface PremiumHook {
  /** All premium tiers in display order. */
  tiers: PremiumTier[];
  /** Whether data has finished loading. */
  loaded: boolean;
}

export interface PremiumSubscribedHook {
  /** Whether the current user has an active subscription (session mirror —
   * see hooks/useBilling.ts for the authoritative real-time read). */
  isSubscribed: boolean;
  /** Ultra benefit icon-card grid items. */
  ultraBenefits: UltraBenefit[];
  /** Whether data has finished loading. */
  loaded: boolean;
}

/** Primary hook: premium tiers for the plans page. */
export function usePremium(): PremiumHook {
  const [tiers, setTiers] = useState<PremiumTier[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    void premiumService.tiers().then((t) => {
      setTiers(t);
      setLoaded(true);
    });
  }, []);

  return { tiers, loaded };
}

/** Subscribed-user hook: ultra benefits + the session's subscribed flag. */
export function usePremiumSubscribed(): PremiumSubscribedHook {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [ultraBenefits, setUltraBenefits] = useState<UltraBenefit[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    void Promise.all([premiumService.isSubscribed(), premiumService.ultraBenefits()]).then(
      ([sub, benefits]) => {
        setIsSubscribed(sub);
        setUltraBenefits(benefits);
        setLoaded(true);
      },
    );
  }, []);

  return { isSubscribed, ultraBenefits, loaded };
}
